import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, Clock, Tag, Building2, Compass, Loader2 } from 'lucide-react';
import { resolveMediaUrl } from '../lib/mediaUrl';
import { fetchJsonCached } from '../lib/settingsCache';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const norm = (v) => (v === null || v === undefined ? '' : String(v)).toLowerCase();

/** Module-level so its identity is stable across renders (an inline default prop
 *  would be a new function every render and would retrigger the memo/effect below). */
const defaultItemLabel = (item) => item?.name;

/**
 * Everything an activity can be found by:
 * title, alias, company, category ("type of activity"), duration ("length of tour"),
 * tags, location, price label and the description text.
 */
const haystack = (a) => [
  a.title,
  a.alias,
  a.seller_name,
  a.duration,
  a.location,
  a.price_display,
  a.price_from,
  a.short_description,
  a.description,
  ...(a.category_names || []),
  ...(a.tags || []),
].map(norm).join(' \u0001 ');

/** All query words must appear somewhere - so "kayak night" still finds a night kayak trip. */
const matches = (text, query) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return words.every((w) => text.includes(w));
};

const Pill = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
    <Icon className="h-3 w-3" />
    {children}
  </span>
);

/**
 * Live activity search used above the All Activities and By Charter Company grids.
 *
 * Filtering is client-side against the published activity list so results update on
 * every keystroke with no request latency. The parent passes `items` (its categories or
 * companies) and gets back the filtered subset via `onFilter`, so the grid narrows too.
 */
const ActivitySearch = ({
  placeholder = 'Search activities, type, length of tour or tags...',
  items = [],
  itemLabel = defaultItemLabel,
  onFilter,
  testId = 'activity-search',
}) => {
  const [query, setQuery] = useState('');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchJsonCached(`${API_URL}/api/public/tours-charters/activities`);
        if (!cancelled) setActivities(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) setActivities([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const indexed = useMemo(
    () => activities.map((a) => ({ activity: a, text: haystack(a) })),
    [activities],
  );

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    return indexed.filter((row) => matches(row.text, q)).map((row) => row.activity).slice(0, 40);
  }, [indexed, query]);

  /** Narrow the parent grid: direct name match, or the item owns a matching activity. */
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    const matchedSlugs = new Set();
    results.forEach((a) => {
      if (a.seller_slug) matchedSlugs.add(a.seller_slug);
      (a.category_slugs || []).forEach((s) => matchedSlugs.add(s));
    });
    return items.filter((item) => {
      const label = norm(itemLabel(item));
      const desc = norm(item.description);
      return matches(`${label} \u0001 ${desc}`, q) || matchedSlugs.has(item.slug);
    });
  }, [items, itemLabel, query, results]);

  /** Signature of the filtered set - lets us skip redundant parent updates.
   *  Without this, handing a fresh array to the parent on every render would set
   *  parent state, re-render this component, and loop forever. */
  const filteredSignature = useMemo(
    () => filteredItems.map((i) => i.id || i.slug).join('|'),
    [filteredItems],
  );
  const lastSent = useRef(null);

  useEffect(() => {
    if (!onFilter) return;
    const key = `${query.trim()}::${filteredSignature}`;
    if (lastSent.current === key) return;
    lastSent.current = key;
    onFilter(filteredItems, query.trim());
  }, [filteredItems, filteredSignature, onFilter, query]);

  const hasQuery = query.trim().length > 0;

  return (
    <div data-testid={testId}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          aria-label="Search activities"
          className="w-full rounded-full border border-slate-300 bg-white py-3.5 pl-12 pr-12 text-slate-900 placeholder-slate-400 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          data-testid={`${testId}-input`}
        />
        {hasQuery && (
          <button
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            aria-label="Clear search"
            data-testid={`${testId}-clear`}
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <p className="mt-2 px-4 text-xs text-slate-500">
        Search by activity name, type of activity, length of tour, tags or charter company.
      </p>

      {hasQuery && (
        <div className="mt-5" data-testid={`${testId}-results`}>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
          ) : results.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center"
              data-testid={`${testId}-no-results`}>
              <p className="font-semibold text-slate-700">
                No activities match &ldquo;{query.trim()}&rdquo;
              </p>
              <p className="mt-1 text-sm text-slate-500">
                {activities.length === 0
                  ? 'Activities are still being added \u2014 try browsing the categories below.'
                  : 'Try a shorter word, a tour length like "2 hours", or a tag.'}
              </p>
            </div>
          ) : (
            <>
              <p className="mb-3 px-1 text-sm font-bold text-slate-700" data-testid={`${testId}-count`}>
                {results.length} {results.length === 1 ? 'activity' : 'activities'} found
              </p>
              <ul className="space-y-3">
                {results.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/activities/view/${a.alias || a.id}`}
                      className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-blue-300 hover:shadow-md"
                      data-testid={`${testId}-result-${a.alias || a.id}`}
                    >
                      <div className="h-20 w-28 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                        {a.images?.[0] ? (
                          <img src={resolveMediaUrl(a.images[0])} alt={a.title} loading="lazy"
                            className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full items-center justify-center text-slate-300">
                            <Compass className="h-7 w-7" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate font-bold text-slate-900">{a.title}</h3>
                        {a.short_description && (
                          <p className="mt-0.5 line-clamp-1 text-sm text-slate-600">{a.short_description}</p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {a.seller_name && <Pill icon={Building2}>{a.seller_name}</Pill>}
                          {a.duration && <Pill icon={Clock}>{a.duration}</Pill>}
                          {(a.category_names || []).slice(0, 2).map((c) => (
                            <Pill key={c} icon={Compass}>{c}</Pill>
                          ))}
                          {(a.tags || []).slice(0, 3).map((t) => (
                            <Pill key={t} icon={Tag}>{t}</Pill>
                          ))}
                        </div>
                      </div>
                      {a.price_display && (
                        <div className="shrink-0 self-center text-right">
                          <p className="text-sm font-bold text-blue-700">{a.price_display}</p>
                        </div>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivitySearch;
